/**
 * ProjectScheduleTab — editable schedule (grid + Gantt bars).
 *
 * Replaces the read-only GanttChart. Every cell in the left grid is editable and
 * every change is saved through useProjectSchedule, which is the one place that
 * talks to the API.
 *
 * Two SVAR behaviours are deliberately worked around:
 *
 *  1. ITask.end is EXCLUSIVE. Binding the Finish column to it displays a task
 *     worked through the 21st as finishing on the 22nd. The column binds to
 *     `end_display` (inclusive) from scheduleAdapter instead.
 *  2. Editor types are "text" | "combo" | "datepicker" | "richselect" |
 *     "multiselect". Anything else silently degrades to a text box — which is
 *     how an earlier probe ended up showing "NaN-NaN-NaN" in a date cell.
 */
import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/style.css';
import { Loader2, Plus, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';
import useProjectSchedule from '../../hooks/useProjectSchedule';
import { toSvarTasks, fromSvarTask, scheduleRange } from '../../lib/scheduleAdapter';

// NOTE: no `scales` prop. SVAR's scale formats are strftime-style (%F %Y),
// NOT date-fns tokens — passing 'MMMM yyyy' printed that string literally in
// the header. Its own defaults render "August 2026" correctly, which the gate
// probe confirmed, so the safest thing is to let it use them.

const STATUS_OPTIONS = [
  { id: 'pending', label: 'Pending' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'blocked', label: 'Blocked' },
];

// Brand mapping. These are the variables that actually paint the chart — read
// out of the shipped stylesheet rather than guessed. --wx-color-primary alone
// only themes generic chrome and leaves the bars default blue.
/**
 * Scoped stylesheet.
 *
 * The variables MUST be declared on `.wx-willow-theme`, not on an ancestor.
 * Willow re-declares them on its own root, which is a descendant of our
 * wrapper, so an outer declaration is overridden — measured in the browser:
 * the wrapper carried #1a1a2e while the bar still resolved #3983eb.
 *
 * The cell rule stops long trade names wrapping to 2–3 lines and overlapping
 * the row beneath (7 of 13 cells overflowed: 36px row, 54px content).
 */
const SCHEDULE_CSS = `
.revolead-gantt .wx-willow-theme {
  --wx-gantt-task-color: #1a1a2e;
  --wx-gantt-task-fill-color: #c9a84c;
  --wx-gantt-task-border-color: #c9a84c;
  --wx-gantt-milestone-color: #c9a84c;
  --wx-gantt-border-color: rgba(201,168,76,0.18);
  --wx-gantt-holiday-background: rgba(0,0,0,0.025);
  --wx-color-primary: #c9a84c;
  --wx-color-font: #1a1a2e;
  --wx-background: #ffffff;
  --wx-background-alt: #f8f7f4;
  --wx-font-family: 'Space Grotesk', system-ui, sans-serif;
  --wx-font-size: 12px;
  --wx-font-size-sm: 11px;
}
.revolead-gantt .wx-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
`;

// 36 not 32: at 32 a two-word Trade ("WALL AND CEILING") wrapped to two lines
// and overlapped the row below. Verified in the browser.
const ROW_HEIGHT = 36;
const SCALE_HEIGHT = 32;
const CELL_WIDTH = 32;

export default function ProjectScheduleTab({ project }) {
  const {
    tasks, loading, saving, addTask, updateTask, deleteTask, reorderTasks,
  } = useProjectSchedule(project?.id);

  // Guards the echo: applying a server response updates our state, which
  // re-renders the Gantt, which would otherwise emit another update-task.
  const applyingRef = useRef(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const svarTasks = useMemo(() => toSvarTasks(tasks), [tasks]);

  // Always supply the window explicitly. With every task unscheduled — the state
  // all existing projects are in — SVAR has no dates to infer a timeline from
  // and crashes reading `.width` off a scale that was never built.
  const range = useMemo(() => scheduleRange(tasks), [tasks]);

  // Widths are deliberately tight. Verified in the browser: at the previous
  // widths the grid pane clipped Start/Finish/Days entirely, so the date cells
  // the UI tells you to edit were off-screen behind a scrollbar.
  const columns = useMemo(() => ([
    { id: 'text', header: 'Task', width: 150, editor: 'text' },
    { id: 'trade', header: 'Trade', width: 105, editor: 'text' },
    {
      id: 'status', header: 'Status', width: 95,
      editor: { type: 'richselect', config: { options: STATUS_OPTIONS } },
    },
    { id: 'start', header: 'Start', width: 92, align: 'center', editor: 'datepicker' },
    // Bound to end_display, NOT end — see the note at the top of this file.
    { id: 'end_display', header: 'Finish', width: 92, align: 'center', editor: 'datepicker' },
    { id: 'duration_days', header: 'Days', width: 48, align: 'center' },
  ]), []);

  // Must match the column widths above, or SVAR sizes the grid pane to its own
  // default and clips the right-hand columns.
  const GRID_WIDTH = 150 + 105 + 95 + 92 + 92 + 48;

  const handleUpdate = useCallback((ev) => {
    if (applyingRef.current) return;
    const id = ev?.id;
    const svarTask = ev?.task;
    if (!id || !svarTask) return;

    const patch = fromSvarTask(svarTask);
    if (Object.keys(patch).length === 0) return;

    applyingRef.current = true;
    try {
      updateTask(id, patch);
    } finally {
      applyingRef.current = false;
    }
  }, [updateTask]);

  const handleDelete = useCallback((ev) => {
    if (ev?.id) deleteTask(ev.id);
  }, [deleteTask]);

  const handleMove = useCallback((ev) => {
    // SVAR reports the new visual order; persist it as sort_order.
    if (!ev || !Array.isArray(ev.tasks)) return;
    reorderTasks(ev.tasks.map(t => t.id));
  }, [reorderTasks]);

  const init = useCallback((api) => {
    if (!api || typeof api.on !== 'function') return;
    api.on('update-task', handleUpdate);
    api.on('delete-task', handleDelete);
    api.on('move-task', handleMove);
  }, [handleUpdate, handleDelete, handleMove]);

  // New tasks start scheduled on today for one day, so a bar exists immediately
  // and can be dragged. Creating them unscheduled left nothing to drag — which
  // reads as "dragging is broken" rather than "there is nothing here yet".
  const handleAdd = useCallback(() => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    addTask({ task_name: 'New task', status: 'pending', start_date: iso, end_date: iso });
  }, [addTask]);

  /** Give every dateless task a real date so its bar appears and can be dragged. */
  const scheduleAllUnscheduled = useCallback(() => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    tasks.filter(t => !t.start_date || !t.end_date)
      .forEach(t => updateTask(t.id, { start_date: iso, end_date: iso }));
  }, [tasks, updateTask]);

  const unscheduledCount = tasks.filter(t => !t.start_date || !t.end_date).length;

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading schedule…
      </div>
    );
  }

  // Fullscreen escapes ProjectDetail's max-w-[1280px], which is too narrow for a
  // programme. Same approach as CostEstimateTab.
  const shellStyle = fullscreen
    ? {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200,
        background: '#f8f7f4', display: 'flex', flexDirection: 'column',
        padding: '16px 24px', overflow: 'hidden',
      }
    : {};

  // Fill the screen when expanded; otherwise size to the rows so the page
  // scrolls naturally instead of the chart being clipped at a fixed height.
  const chartHeight = fullscreen
    ? '100%'
    : Math.min(Math.max(tasks.length * ROW_HEIGHT + SCALE_HEIGHT + 24, 240), 640);

  return (
    <div className="space-y-3" style={shellStyle}>
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Schedule ({tasks.length})
          </h3>
          <span className="text-xs" style={{ color: saving ? '#c9a84c' : '#9ca3af' }}>
            {saving ? 'Saving…' : 'All changes saved automatically'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setFullscreen(f => !f)}
            size="sm"
            variant="outline"
            title={fullscreen ? 'Exit full screen (Esc)' : 'Expand to full screen'}
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button onClick={handleAdd} size="sm" className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold">
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Bars only exist for tasks with dates. Every imported task starts
          without them, so without this the timeline looks empty and dragging
          appears broken. */}
      {unscheduledCount > 0 && tasks.length > 0 && (
        <div
          className="flex items-center justify-between gap-3 px-3 py-2 rounded-md flex-shrink-0"
          style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}
        >
          {/* Wording verified against the real UI: SVAR DOES draw a placeholder
              bar for an unscheduled task, parked at the start of the window. It
              is not a real date, which is the thing worth saying. */}
          <span className="text-xs" style={{ color: '#1a1a2e' }}>
            <strong>{unscheduledCount}</strong> {unscheduledCount === 1 ? 'task has' : 'tasks have'} no dates yet.
            {unscheduledCount === 1 ? ' Its bar is' : ' Their bars are'} parked at the left and
            {unscheduledCount === 1 ? ' does' : ' do'} not mean a real start date — set Start / Finish to place
            {unscheduledCount === 1 ? ' it' : ' them'}, or:
          </span>
          <Button onClick={scheduleAllUnscheduled} size="sm" variant="outline" className="flex-shrink-0">
            Start all today
          </Button>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="text-center py-16 rounded-md" style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}>
          <p className="text-[#6b7280] text-lg mb-2">No tasks yet</p>
          <p className="text-[#6b7280]/60 text-sm mb-6">Add a task, then set its dates in the grid</p>
          <Button onClick={handleAdd} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        </div>
      ) : (
        <div
          className="rounded-md flex-1 min-h-0"
          style={{ border: '1px solid #e5e4df', background: '#fff' }}
        >
          <div className="revolead-gantt" style={{ height: chartHeight }}>
            <style>{SCHEDULE_CSS}</style>
            <Willow>
              <Gantt
                tasks={svarTasks}
                links={[]}
                columns={columns}
                unscheduledTasks
                start={range.start}
                end={range.end}
                gridWidth={GRID_WIDTH}
                cellWidth={CELL_WIDTH}
                cellHeight={ROW_HEIGHT}
                scaleHeight={SCALE_HEIGHT}
                init={init}
              />
            </Willow>
          </div>
        </div>
      )}

      <p className="text-xs" style={{ color: '#9ca3af' }}>
        Click a cell to edit · drag a bar to move it · drag a bar edge to change its length.
        Tasks with no dates are listed as unscheduled.
      </p>
    </div>
  );
}
